import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useConferenceSync from '../../../hooks/useConferenceSync';
import { useReveilBackend } from '../../../hooks/useSalle';
import { parseConducteur, minutageParSlide, verifierCouverture, positionCurseur, ETIREMENT_MINUTAGE } from './parseConducteur';
import RenduConducteur from './RenduConducteur';
import { PLACE_MARGE } from './MargeConducteur';
import ReleveTimings from './ReleveTimings';
import CompteurSalle from './CompteurSalle';

/**
 * PROMPTEUR — s'ouvre dans un second onglet, suit le diaporama.
 *
 * CE QU'IL RÉSOUT. Le conducteur fait 1300 lignes. Sur scène, entre deux
 * phrases, on ne cherche pas dans un document : on lève les yeux et on doit
 * tomber sur sa ligne. Le prompteur suit donc la slide affichée dans l'autre
 * onglet et amène la bonne section sous les yeux, sans qu'on ait à y toucher.
 *
 * TROIS PARTIS PRIS, ET ILS VIENNENT DE LA SCÈNE :
 *
 *  1. **On ne masque pas le reste.** Un prompteur qui n'affiche que la section
 *     courante rend impossible le coup d'œil en avant — « qu'est-ce qui vient
 *     après ? » — qui est exactement ce qu'on fait quand on sent qu'on est en
 *     retard. Tout le conducteur est là ; la section courante est éclairée, le
 *     reste est estompé.
 *  2. **Le défilement n'enferme jamais, ET il ne se perd pas.** Dès qu'on fait
 *     défiler à la main, le suivi automatique se coupe et un bouton « revenir »
 *     apparaît : rien n'est plus désagréable qu'un document qui se rembobine
 *     tout seul pendant qu'on y cherche quelque chose. Mais il se RÉARME au
 *     changement de slide — sinon un seul coup de molette, à la deuxième
 *     minute, tuait le suivi pour les quarante-cinq restantes.
 *  3. **Le chrono compare à la FIN prévue de la slide**, parce que la vraie
 *     contrainte de cette conférence est la durée : ~46 min de matière pour un
 *     créneau de 60 dont on ne sait pas s'il est net de questions. L'écart est
 *     un compte à rebours — négatif tant qu'il reste du temps sur la slide,
 *     positif dès qu'on déborde. Vert si on est large, ambre au-delà d'une
 *     minute de dépassement.
 *  4. **La flèche droite peut FORCER la slide suivante.** Sur les trois slides
 *     d'usine, elle déroule d'abord la séquence de la simulation — la visite du
 *     plateau, puis les cinq tours — et c'est ce qu'on veut en conférence. En
 *     répétition, c'est exactement l'inverse : on reprend au milieu, et il
 *     faudrait re-dérouler une manche entière pour atteindre la slide d'après.
 *     « ⏭ passer » saute la séquence et change de slide, tout de suite.
 *  5. **Le chrono se recale sur la slide**, et ça ne sert qu'aux répétitions
 *     — mais les répétitions sont la seule chose qu'on fait vingt fois. On ne
 *     répète jamais depuis le début : on reprend au milieu, sur la slide qui
 *     coince. Sans recalage, l'écart affiché mesure alors l'heure de départ de
 *     la répétition et pas le rythme, donc il ne veut plus rien dire. « Caler »
 *     remet le chrono au DÉBUT prévu de la slide affichée : on repart d'un
 *     retard nul, et l'écart redevient ce qu'il doit être — le temps pris sur
 *     cette slide-là.
 *  6. **Le chrono se met en pause**, et il fallait bien y venir : entre une
 *     question qui s'éternise et un incident technique, le temps qui continue
 *     de courir transforme le seul repère fiable de la scène en mensonge. La
 *     pause est un bouton ET la touche `p`, parce qu'on ne vise pas un bouton
 *     de douze pixels en parlant.
 *  7. **Un curseur de lecture montre où l'on devrait en être** (14/09). Il
 *     part du haut de la slide quand on y arrive, et descend au rythme prévu
 *     pour elle — voir `positionCurseur` pour pourquoi ce n'est pas l'horaire
 *     absolu. Le défilement le suit, PAR À-COUPS et jamais en continu : un
 *     texte qui glisse pendant qu'on le lit ne se lit plus. Quand le curseur
 *     passe les deux tiers de l'écran, la zone remonte pour le ramener au
 *     tiers, puis ne bouge plus. Et le curseur peut avoir tort — on parle
 *     plus vite certains jours : cliquer un paragraphe dit « je suis ici », et
 *     il repart de là, au même rythme.
 *  8. **Le relevé enregistre la répétition** (16/09). Les horaires du
 *     conducteur ont été écrits à l'estime, et le curseur de lecture s'y cale :
 *     tant qu'ils sont faux, le prompteur court sur une slide et rampe sur la
 *     suivante. Un bouton — et la touche `e` — horodate chaque changement de
 *     slide au chrono ; on récupère les vraies durées ET le débit réellement
 *     tenu, qui est la seule donnée capable de corriger les slides qu'on n'a
 *     pas répétées. Voir `ReleveTimings.jsx`. Le relevé survit à un
 *     rechargement : une répétition de quarante minutes ne se refait pas
 *     pour un F5.
 *  9. **Tout se joue à la flèche suivante** (24/09). La télécommande de scène
 *     n'a que « suivant » et « précédent ». La voix IA et l'analyse des
 *     questions ne sont donc plus des boutons à viser : sur une slide qui en
 *     porte une, le premier appui la joue, le second change de slide. Le « → »
 *     du bandeau prend la couleur de ce qui va se passer, et les repères du
 *     conducteur s'affichent « → voix » / « → analyse ». Voir
 *     `handleSlideNavigation` dans ConferencePage.
 *
 * ⚠️ THÈME CLAIR (08/09) — fond blanc, texte noir, bandeau compris. Voir la
 * note de hiérarchie en tête de `RenduConducteur.jsx` : elle a dû être refaite,
 * pas seulement inversée. Le seul accent coloré du bandeau est l'ÉCART au
 * minutage, parce que c'est la seule information qu'on doit voir sans la
 * chercher.
 *
 * ⚠️ MÊME ORIGINE OBLIGATOIRE. Ouvrir le prompteur sur `127.0.0.1` pendant que
 * le diaporama est sur `localhost` casse la synchro sans erreur visible : le
 * bandeau affiche donc l'état de connexion en permanence.
 */

const mmss = (s) => {
    const a = Math.abs(Math.round(s));
    return `${Math.floor(a / 60)}:${String(a % 60).padStart(2, '0')}`;
};

/** Le chrono à l'instant, lu dans une ref : les effets qui en ont besoin ne
    doivent pas se relancer à chaque pas du chrono. */
const lireChrono = (ref) => ref.current.cumul + (ref.current.demarre ? (Date.now() - ref.current.demarre) / 1000 : 0);

/** Où dort le relevé entre deux ouvertures de l'onglet. */
const cleReleve = (id) => `prompteur:releve:${id}`;

export default function PrompterPage({ slides = [], conferenceId = 'seminaire', chemin, salle = null }) {
    const [markdown, setMarkdown] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [num, setNum] = useState(null);          // numéro de slide courant
    const [relie, setRelie] = useState(false);
    const [suivi, setSuivi] = useState(true);      // le défilement suit-il ?
    const [taille, setTaille] = useState(23);      // px, réglable sur scène
    /* LE CHRONO EN TROIS MORCEAUX, parce qu'il peut se mettre en pause.
       `demarre` est le début du segment EN COURS (null = à l'arrêt), `cumul`
       les secondes déjà capitalisées par les segments précédents. Un simple
       horodatage de départ ne suffisait plus : mis à null pour marquer la
       pause, il devenait indistinguable de « pas encore démarré » et le
       démarrage automatique relançait le chrono à la slide suivante. D'où
       `dejaLance`, qui n'est pas un état d'affichage et n'a donc pas à
       provoquer de rendu. */
    const [demarre, setDemarre] = useState(null);
    const [cumul, setCumul] = useState(0);
    const [maintenant, setMaintenant] = useState(Date.now());
    const dejaLance = useRef(false);
    const chronoRef = useRef({ cumul: 0, demarre: null });
    chronoRef.current = { cumul, demarre };
    /* L'ANCRE DU CURSEUR : sur quelle slide, à quel temps de chrono, et depuis
       quel point du texte il est parti. `poids: null` veut dire « du début de
       la slide » — le point exact n'est connu qu'une fois le conducteur lu.
       Elle bouge à chaque changement de slide, à chaque recalage du chrono, et
       quand on clique un paragraphe. La pause, elle, n'y touche pas : le
       chrono s'arrête, donc le curseur aussi. */
    const [ancre, setAncre] = useState(null);

    /* LE RELEVÉ. `marques` est la suite des arrivées sur une slide, horodatées
       au chrono : `{num, t}`. Une marque `num: null` est une fin de segment,
       posée à l'arrêt — elle borne la dernière slide dite et empêche une
       reprise ultérieure de fabriquer une durée à cheval sur la coupure.
       Relu du stockage au démarrage : perdre une répétition entière sur un
       rechargement accidentel serait le seul vrai défaut de cet outil. */
    const [enregistre, setEnregistre] = useState(false);
    const [marques, setMarques] = useState(() => {
        try {
            const brut = window.localStorage.getItem(cleReleve(conferenceId));
            const lu = brut ? JSON.parse(brut) : [];
            return Array.isArray(lu) ? lu : [];
        } catch {
            return [];
        }
    });
    const [releveOuvert, setReleveOuvert] = useState(false);

    /* Chaque changement de slide réveille le backend (Render s'endort après
       15 min d'inactivité, et met jusqu'à 5 min à repartir). Une conférence
       change de slide toutes les 1 à 2 minutes : le rythme de la scène suffit
       à le tenir éveillé, sans qu'aucun compteur ne batte. Voir
       `useReveilBackend` pour le filet qui couvre les longues stations —
       l'accueil, et les questions-réponses sur la slide 34. */
    useReveilBackend(num);

    const conteneurRef = useRef(null);
    const refsSections = useRef({});
    const filetRef = useRef(null);
    const defilementAuto = useRef(false);
    const minuteurDefilement = useRef(null);
    const recentrerRef = useRef(() => {});

    const { sections, parSlide, avertissements } = useMemo(() => parseConducteur(markdown), [markdown]);
    const couverture = useMemo(
        () => (markdown && slides.length ? verifierCouverture(parSlide, slides) : null),
        [markdown, parSlide, slides]
    );
    /* La frise des horaires attendus. Elle est construite pour TOUTES les
       slides, pas seulement celles dont la section porte un minutage — voir
       `minutageParSlide`. Étirée de `ETIREMENT_MINUTAGE` : c'est le réglage
       de la vitesse du curseur. */
    const minutage = useMemo(
        () => minutageParSlide(sections, slides.map((s) => s.num).filter(Boolean), ETIREMENT_MINUTAGE),
        [sections, slides]
    );

    // --- le conducteur ------------------------------------------------------
    useEffect(() => {
        let vivant = true;
        fetch(chemin)
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.text();
            })
            .then((t) => { if (vivant) setMarkdown(t); })
            .catch((e) => { if (vivant) setErreur(e.message); });
        return () => { vivant = false; };
    }, [chemin]);

    // --- la synchro ---------------------------------------------------------
    /* ⚠️ UN SEUL DIAPORAMA EST PILOTÉ, MÊME SI PLUSIEURS SONT OUVERTS (23/09).
       Le canal est partagé par tous les onglets de la conférence. Avec un
       deuxième diaporama ouvert en arrière-plan, chaque flèche partait dans les
       deux, et le prompteur affichait le numéro du dernier qui avait répondu.
       Or un onglet caché ne dessine plus : sa simulation reste « occupée » au
       milieu de la manche III, avale toutes les flèches droites et reste
       bloqué sur la slide 14. Au premier `←`, il passait en 13 — et le
       prompteur sautait en 13 pendant que l'écran projeté était en 26.
       On suit donc UNE source : de préférence celle qui est à l'écran
       (`visible`), sinon celle qu'on suivait déjà. Les ordres partent avec
       `cible`, et les autres diaporamas les ignorent. */
    const sourcesRef = useRef(new Map());   // id → { num, visible, action }
    const cibleRef = useRef(null);
    const [nbDiaporamas, setNbDiaporamas] = useState(0);
    /* Ce que jouera le prochain « suivant » sur le diaporama suivi : `"f1"`
       (la voix IA), `"synthese"` (l'analyse des questions), ou null — il
       change de slide. Voir `handleSlideNavigation` dans ConferencePage. */
    const [actionSuivante, setActionSuivante] = useState(null);
    /* Le QR en grand est-il à l'écran ? Le diaporama le dit dans `etat` : il
       peut le refermer seul (un clic dessus, ou la vidéo de la voix IA), et le
       bouton resterait allumé devant un écran sans QR. Voir `basculerQr`. */
    const [qrOuvert, setQrOuvert] = useState(false);

    const surMessage = useCallback((msg) => {
        if (!msg || typeof msg !== 'object') return;
        const sources = sourcesRef.current;
        if (msg.t === 'etat') {
            const id = msg.source ?? 'anonyme';
            sources.set(id, { num: msg.num ?? null, visible: msg.visible !== false, action: msg.action ?? null, qr: msg.qr });
            const actuelle = sources.get(cibleRef.current);
            if (!actuelle || (msg.visible !== false && !actuelle.visible)) cibleRef.current = id;
            setNbDiaporamas(sources.size);
            setRelie(true);
            setNum(sources.get(cibleRef.current).num);
            setActionSuivante(sources.get(cibleRef.current).action);
            // Un diaporama d'avant le 24/09 n'envoie pas `qr` : on garde l'état local.
            const qr = sources.get(cibleRef.current).qr;
            if (typeof qr === 'boolean') setQrOuvert(qr);
        } else if (msg.t === 'synthese') {
            /* La flèche de la slide 31 lance l'analyse des questions. Elle
               part d'ici parce que c'est le prompteur qui tient la session
               admin et l'état de la génération (CompteurSalle). Un diaporama
               qu'on ne suit pas n'a pas voix au chapitre. */
            if ((msg.source ?? 'anonyme') !== cibleRef.current) return;
            window.dispatchEvent(new CustomEvent('salle:synthese', { detail: { parFleche: true } }));
        } else if (msg.t === 'adieu') {
            sources.delete(msg.source ?? 'anonyme');
            setNbDiaporamas(sources.size);
            if (!sources.has(cibleRef.current)) {
                const restantes = [...sources.entries()];
                const suivante = restantes.find(([, s]) => s.visible) || restantes[0];
                cibleRef.current = suivante ? suivante[0] : null;
                if (suivante) setNum(suivante[1].num);
                setActionSuivante(suivante ? suivante[1].action : null);
            }
            setRelie(sources.size > 0);
        }
    }, []);

    const envoyerCanal = useConferenceSync(conferenceId, surMessage);
    const envoyer = useCallback(
        (msg) => envoyerCanal(msg.t === 'bonjour' ? msg : { ...msg, cible: cibleRef.current }),
        [envoyerCanal]
    );

    useEffect(() => { envoyer({ t: 'bonjour' }); }, [envoyer]);

    useEffect(() => {
        const id = setInterval(() => setMaintenant(Date.now()), 500);
        return () => clearInterval(id);
    }, []);

    /* ⚠️ LIBÉRER LE VERROU EN MÊME TEMPS QUE SON MINUTEUR. Annuler le minuteur
       sans remettre `defilementAuto` à faux le laissait vrai pour toujours si
       le nettoyage tombait pendant un défilement — et le suivi du curseur,
       qui s'abstient tant que ce verrou est pris, ne défilait plus jamais.
       Observé au rechargement à chaud ; le même trou existerait à tout
       remontage. */
    useEffect(() => () => {
        window.clearTimeout(minuteurDefilement.current);
        defilementAuto.current = false;
    }, []);

    /* L'heure à laquelle la slide COURANTE devait commencer : son début
       déclaré dans le conducteur, sinon la fin de celle d'avant. Voir le
       `debut` renvoyé par `minutageParSlide`. */
    const debutPrevu = num != null && minutage.debut[num] != null ? minutage.debut[num] : null;

    /* ⚠️ L'ANCRE SUIT LA SLIDE. Déclaré AVANT le démarrage automatique : les
       deux se déclenchent sur la même arrivée en slide 2, et c'est l'ancre du
       démarrage — posée sur l'horaire de départ — qui doit gagner. */
    useEffect(() => {
        if (num == null) return;
        setAncre({ num, t: lireChrono(chronoRef), poids: null });
    }, [num]);

    /* Le chrono démarre tout seul dès qu'on quitte la slide de titre : un
       chrono qu'on oublie de lancer ne sert à rien, et le lancer à la main au
       moment de monter sur scène est exactement ce qu'on oublie de faire. UNE
       SEULE FOIS : sans ce garde-fou, la première pause serait annulée par le
       changement de slide suivant.

       ⚠️ IL DÉMARRE À L'HEURE PRÉVUE DE LA SLIDE, PAS À ZÉRO (14/09). Il
       partait de 0:00 en arrivant sur la slide 2, prévue à 1:20 : la slide 1
       n'était jamais comptée, et le badge affichait 1:20 d'avance fictive
       pendant toute la conférence. Il part maintenant du début prévu de la
       slide où l'on arrive — 1:20 en séance, et le bon horaire en répétition
       si le prompteur s'ouvre en cours de route. D'où l'attente du
       conducteur : c'est lui qui porte les horaires. */
    useEffect(() => {
        if (dejaLance.current || num == null || num <= 1 || markdown === null) return;
        dejaLance.current = true;
        const depart = minutage.debut[num] ?? 0;
        setCumul(depart);
        setDemarre(Date.now());
        setAncre({ num, t: depart, poids: null });
    }, [num, markdown, minutage]);

    // --- le curseur de lecture ----------------------------------------------
    const sectionCourante = num != null ? parSlide[num] : null;
    const cleCourante = sectionCourante ? `${sectionCourante.de}-${sectionCourante.a}-${sectionCourante.niveau}` : null;

    const ecoule = cumul + (demarre ? (maintenant - demarre) / 1000 : 0);
    const chronoLance = demarre !== null || cumul > 0;
    // Au rendu qui suit un changement de slide, l'ancre n'a pas encore suivi :
    // on part du début de la nouvelle slide plutôt que de n'afficher rien.
    const ancreCourante = ancre && ancre.num === num ? ancre : { num, t: ecoule, poids: null };
    const dureeSlide = debutPrevu != null && minutage.attendu[num] != null ? minutage.attendu[num] - debutPrevu : null;
    const curseur = chronoLance && sectionCourante
        ? positionCurseur(sectionCourante, num, ancreCourante.poids, ecoule - ancreCourante.t, dureeSlide)
        : null;
    const cleCurseur = curseur ? `${cleCourante}:${curseur.bloc}:${curseur.fraction.toFixed(3)}` : null;

    /** Cliquer un paragraphe : « je suis ici ». Le curseur repart de ce bloc,
        au rythme de la slide. Ça vaut aussi pour le bouton F1 écrit dans le
        texte : appuyer dessus, c'est être à cet endroit. */
    const choisirBloc = useCallback((i) => {
        if (num == null || !sectionCourante) return;
        setAncre({ num, t: lireChrono(chronoRef), poids: sectionCourante.cumuls[i] });
        setSuivi(true);
    }, [num, sectionCourante]);

    // --- défilement ---------------------------------------------------------
    /**
     * Faire défiler la zone de lecture d'un décalage donné.
     *
     * ⚠️ ON VISE LE CONTENEUR, EXPLICITEMENT. C'était `el.scrollIntoView()`, et
     * `conteneurRef` était déclaré sans jamais être lu : on laissait le
     * navigateur choisir lui-même quel ancêtre faire défiler. Il peut en
     * choisir un autre que la zone de lecture — la section devenait active,
     * le surlignage suivait, et rien ne bougeait sous les yeux.
     */
    const defilerDe = useCallback((delta, doux = true) => {
        const conteneur = conteneurRef.current;
        if (!conteneur) return;
        defilementAuto.current = true;
        conteneur.scrollTo({
            top: Math.max(0, conteneur.scrollTop + delta),
            behavior: doux ? 'smooth' : 'auto'
        });
        // Tant que l'animation court, le suivi du curseur ne mesure rien : il
        // lirait une position intermédiaire et relancerait un second
        // défilement par-dessus le premier. 900 ms et non 700 : sur un saut de
        // chapitre, l'animation douce est plus longue que la fenêtre qu'on lui
        // laissait.
        window.clearTimeout(minuteurDefilement.current);
        minuteurDefilement.current = window.setTimeout(() => { defilementAuto.current = false; }, 900);
    }, []);

    /**
     * Amener une section en haut de la zone de lecture.
     *
     * Le calcul passe par `getBoundingClientRect` et non `offsetTop` : ce
     * dernier se mesure depuis l'`offsetParent`, qui n'est pas le conteneur
     * tant que celui-ci n'est pas positionné. La soustraction de rects, elle,
     * est vraie quelle que soit la mise en page.
     */
    const defilerVers = useCallback((cle, doux = true) => {
        const conteneur = conteneurRef.current;
        const el = cle && refsSections.current[cle];
        if (!conteneur || !el) return;
        defilerDe(el.getBoundingClientRect().top - conteneur.getBoundingClientRect().top - 4, doux);
    }, [defilerDe]);

    /* Recentrer, c'est aller au curseur s'il est engagé dans la slide, sinon
       au titre de la section. Le titre reste la cible tant que le curseur est
       sur le premier bloc dit : c'est ce qui garde « Slide 12 — La phrase
       unique » en haut de l'écran à chaque changement de slide, comme avant
       le curseur. Il cesse de l'être sur la seconde slide d'une section qui
       en couvre deux (« SLIDES 30 → 31 ») : là, le curseur est au milieu, et
       remonter au titre montrerait ce qu'on vient de dire.
       Réassignée à chaque rendu, pour que les effets lisent le curseur du
       rendu en cours sans avoir à en dépendre. */
    recentrerRef.current = () => {
        const conteneur = conteneurRef.current;
        const filet = filetRef.current;
        if (curseur && !curseur.enTete && conteneur && filet) {
            const haut = conteneur.getBoundingClientRect();
            defilerDe(filet.getBoundingClientRect().top - haut.top - haut.height / 3);
            return;
        }
        defilerVers(cleCourante);
    };

    /* ⚠️ `suivi` N'EST PLUS DANS LES DÉPENDANCES (14/09). Il y était pour que
       le réarmement au changement de slide relance le défilement ; mais le
       changement de slide relance déjà cet effet par `num`. Et depuis que
       cliquer un paragraphe réarme le suivi, le garder ici renvoyait la page
       au titre de la section à chaque clic. */
    useEffect(() => {
        if (!cleCourante) return;
        recentrerRef.current();
    }, [cleCourante, num, markdown]);

    /* LE DÉFILEMENT PAR À-COUPS. Rien ne bouge tant que le filet est dans les
       deux premiers tiers de l'écran ; au-delà — ou s'il est sorti par le
       haut — la zone remonte pour le remettre au tiers. On relit donc un
       paragraphe entier sans que le texte ne glisse, puis on retrouve sa
       ligne au même endroit de l'écran. */
    useEffect(() => {
        if (!suivi || cleCurseur == null || defilementAuto.current) return;
        const conteneur = conteneurRef.current;
        const filet = filetRef.current;
        if (!conteneur || !filet) return;
        const haut = conteneur.getBoundingClientRect();
        const y = filet.getBoundingClientRect().top - haut.top;
        if (y >= 0 && y <= (haut.height * 2) / 3) return;
        defilerDe(y - haut.height / 3);
    }, [cleCurseur, suivi, defilerDe]);

    /* ⚠️ LE SUIVI SE RÉARME À CHAQUE CHANGEMENT DE SLIDE, et c'est délibéré.
       Avant, un seul coup de molette le coupait pour le reste de la séance : on
       fait défiler pour jeter un œil à ce qui vient — le geste le plus naturel
       du monde quand on sent qu'on est en retard —, et le prompteur ne suivait
       plus jamais. Le bouton « revenir » existait, mais il faut y penser, en
       parlant, devant quarante personnes.
       Avoir fait défiler à la main veut dire « je cherche quelque chose
       MAINTENANT », pas « ne me suis plus de la soirée ». Passer la slide dit
       qu'on a fini de chercher. Le bouton reste utile pour revenir sans
       changer de slide. */
    useEffect(() => {
        if (num == null) return;
        setSuivi(true);
    }, [num]);

    /* La molette et le doigt sont toujours des gestes humains — un défilement
       programmé ne les déclenche pas. Ils coupent donc le suivi même pendant
       un défilement automatique : c'est la main qui a raison, et un à-coup du
       curseur ne doit pas l'annuler. */
    const surDefilement = () => setSuivi(false);

    const revenir = () => {
        setSuivi(true);
        recentrerRef.current();
    };

    // --- piloter le diaporama depuis le prompteur ---------------------------
    /* `forcer` court-circuite la séquence de scène de l'usine côté diaporama :
       voir la note sur `navigationRef` dans ConferencePage. Sans lui, la flèche
       droite déroule la simulation avant de changer de slide — ce qui est le
       bon comportement en séance, et le mauvais en répétition. */
    const naviguer = useCallback(
        (delta, forcer = false) => envoyer({ t: 'nav', delta, forcer }),
        [envoyer]
    );

    /* F1 déclenche l'action contextuelle de la slide sur le DIAPORAMA — la voix
       IA la plupart du temps. Le prompteur ne la joue pas lui-même : il n'a ni
       l'audio, ni le contexte de la slide, et le son doit sortir de la machine
       branchée sur la sono, pas de l'écran de retour.

       Pourquoi c'est utile ici : les quatre répliques de la voix IA se
       déclenchent pile aux moments où le conducteur dit « Puis **F1**. » —
       c'est-à-dire pendant qu'on lit le prompteur. Devoir retourner sur
       l'onglet du diaporama pour appuyer sur une touche, c'est le seul geste
       de la conférence qui obligeait à quitter son texte des yeux. */
    const declencherF1 = useCallback(() => envoyer({ t: 'f1' }), [envoyer]);

    /* LE QR DES QUESTIONS, EN GRAND, SUR L'ÉCRAN PROJETÉ. Une salle ne répond
       pas toute en même temps : quelqu'un arrive en retard, un autre veut
       poser sa question à la minute 35 et a refermé la page. Revenir à la
       slide 3 pour eux casserait le déroulé, et la barre de progression
       annoncerait à toute la salle qu'on recule. Le QR s'ouvre donc n'importe
       quand, par-dessus la slide affichée, sans rien déplacer dessous.
       Le prompteur l'ouvre et le ferme ; le diaporama, qui peut aussi le
       refermer seul, renvoie l'état réel dans `etat` (`qrOuvert`, plus haut). */
    const basculerQr = useCallback(() => {
        setQrOuvert((ouvert) => {
            envoyer({ t: 'qr', actif: !ouvert });
            return !ouvert;
        });
    }, [envoyer]);

    // --- chrono : marche / pause / remise à zéro ----------------------------
    const enMarche = demarre !== null;

    /** Bascule marche/pause en capitalisant le segment écoulé.
        ⚠️ NE PAS REPLIER ÇA DANS UN UPDATER `setDemarre((d) => …)` qui
        appellerait `setCumul` : StrictMode invoque les updaters deux fois en
        développement, et le segment serait compté deux fois à chaque pause. */
    const basculerChrono = useCallback(() => {
        if (demarre === null) {
            dejaLance.current = true;
            setDemarre(Date.now());
            return;
        }
        setCumul((c) => c + (Date.now() - demarre) / 1000);
        setDemarre(null);
    }, [demarre]);

    /** Cale le chrono sur le DÉBUT prévu de la slide affichée.
        Le début et non la fin : au moment où on appuie, on ARRIVE sur la
        slide, on ne l'a pas encore dite. Se caler sur la fin afficherait un
        écart nul alors qu'il reste toute la slide à faire — l'inverse de ce
        qu'on cherche à mesurer. Le curseur repart du haut de la slide, pour
        la même raison.
        ⚠️ Ce bouton ne touche PAS l'état marche/pause : une pause est une
        décision, et un bouton qui règle une valeur n'a pas à la révoquer. Seule
        exception, un chrono jamais lancé : là, « caler » veut évidemment dire
        « cale-toi et pars ». */
    const realigner = useCallback(() => {
        if (debutPrevu == null) return;
        const enPause = demarre === null && dejaLance.current;
        dejaLance.current = true;
        setCumul(debutPrevu);
        setDemarre(enPause ? null : Date.now());
        setAncre({ num, t: debutPrevu, poids: null });
    }, [debutPrevu, demarre, num]);

    // --- relevé : enregistrer la répétition ---------------------------------
    /* ⚠️ LA MARQUE EST POSÉE PAR EFFET, PAS DANS `naviguer`. La slide peut
       changer sans passer par le prompteur — c'est même le cas normal, puisque
       le diaporama est piloté depuis l'autre onglet et que la synchro nous
       renvoie le numéro. Seul `num` voit tous les changements.
       Le garde sur la dernière marque rend l'effet idempotent : StrictMode
       l'invoque deux fois en développement, et sans lui chaque slide serait
       comptée en double avec une durée nulle. */
    useEffect(() => {
        if (!enregistre || num == null) return;
        const t = lireChrono(chronoRef);
        setMarques((m) => {
            const dernier = m[m.length - 1];
            if (dernier && dernier.num === num) return m;
            return [...m, { num, t }];
        });
    }, [enregistre, num]);

    useEffect(() => {
        try {
            window.localStorage.setItem(cleReleve(conferenceId), JSON.stringify(marques));
        } catch {
            /* Navigation privée, quota plein : le relevé vit quand même en
               mémoire pour cette session. Ce n'est pas une raison d'échouer. */
        }
    }, [marques, conferenceId]);

    /** Démarrer, ou arrêter en bornant la dernière slide.
        Relancer après un arrêt REPREND le même relevé : on coupe souvent une
        répétition en deux, et deux relevés à recoller à la main ne valent rien.
        « effacer », dans le panneau, est le geste explicite pour repartir. */
    const basculerEnregistrement = useCallback(() => {
        if (!enregistre) {
            /* ⚠️ « REC » LANCE LE CHRONO S'IL N'A JAMAIS TOURNÉ, à zéro. Sans
               ça, c'est le démarrage automatique qui s'en charge en quittant la
               slide de titre — et il pose le chrono à l'heure PRÉVUE de la
               slide d'arrivée (1:20). La slide 1 se mesurerait alors exactement
               conforme au plan quoi qu'il arrive, et tout le relevé serait
               décalé d'une minute vingt. Une répétition se chronomètre depuis
               son vrai début, sinon elle ne mesure rien.
               `chronoRef` est mis à jour dans la foulée : la marque de la slide
               courante est posée par un effet du même commit, et l'état, lui,
               n'est pas encore rendu. */
            if (!dejaLance.current) {
                const t0 = Date.now();
                dejaLance.current = true;
                setCumul(0);
                setDemarre(t0);
                chronoRef.current = { cumul: 0, demarre: t0 };
                setAncre({ num, t: 0, poids: null });
            }
            setEnregistre(true);
            return;
        }
        const t = lireChrono(chronoRef);
        setMarques((m) => (m.length === 0 || m[m.length - 1].num == null ? m : [...m, { num: null, t }]));
        setEnregistre(false);
    }, [enregistre, num]);

    const effacerReleve = useCallback(() => {
        setMarques([]);
        setEnregistre(false);
    }, []);

    /** Remet à zéro ET relance : sur scène, « ⟲ » veut dire « on repart ». */
    const rembobiner = useCallback(() => {
        dejaLance.current = true;
        setCumul(0);
        setDemarre(Date.now());
        setAncre({ num, t: 0, poids: null });
    }, [num]);

    useEffect(() => {
        const surTouche = (e) => {
            // Maj+→ force la slide suivante sans dérouler la séquence d'usine.
            // Le modificateur plutôt qu'une lettre : c'est la même action que la
            // flèche, en plus brutal, et ça se retient sans rien apprendre.
            if (e.key === 'ArrowRight' || e.key === 'PageDown') { naviguer(1, e.shiftKey); e.preventDefault(); }
            else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { naviguer(-1); e.preventDefault(); }
            // Aucun champ de saisie sur cette page : une lettre nue est sans
            // risque, et c'est tout l'intérêt — la pause doit se prendre sans
            // viser quoi que ce soit.
            else if (e.key === 'p' || e.key === 'P') { basculerChrono(); e.preventDefault(); }
            else if (e.key === 'r' || e.key === 'R') { realigner(); e.preventDefault(); }
            // `e` comme enregistrer. Même raison que `p` : on lance une
            // répétition sans quitter son texte des yeux.
            else if (e.key === 'e' || e.key === 'E') { basculerEnregistrement(); e.preventDefault(); }
            // ⚠️ `preventDefault` est indispensable : F1 ouvre l'aide du
            // navigateur, et une fenêtre d'aide par-dessus le prompteur en
            // pleine conférence est exactement ce qu'on ne peut pas se
            // permettre.
            else if (e.key === 'F1') { declencherF1(); e.preventDefault(); }
            else if (e.key === '+') setTaille((t) => Math.min(48, t + 1));
            else if (e.key === '-') setTaille((t) => Math.max(13, t - 1));
        };
        window.addEventListener('keydown', surTouche);
        return () => window.removeEventListener('keydown', surTouche);
    }, [naviguer, declencherF1, basculerChrono, realigner, basculerEnregistrement]);

    // --- écart à la fin prévue de la slide ----------------------------------
    /* `minutage.attendu[n]` est l'heure à laquelle la slide doit être TERMINÉE.
       L'écart est donc un compte à rebours : négatif = il reste du temps. */
    const prevu = num != null && minutage.attendu[num] != null ? minutage.attendu[num] : null;
    const prevuExact = num != null && minutage.exact[num] === true;
    const ecart = prevu != null && chronoLance ? ecoule - prevu : null;

    const totalSlides = slides.length;
    const slideCourante = slides.find((s) => s.num === num);
    /* Les slides qui portent la simulation. `usine` est le numéro de PASSAGE,
       posé sur la slide ; c'est lui qui dit qu'une séquence va se dérouler ici
       et donc que « passer » a un sens. */
    const estUsine = slideCourante?.usine != null;

    if (erreur) {
        return (
            <div className="w-full h-screen bg-white text-slate-800 flex items-center justify-center p-10">
                <div className="max-w-2xl">
                    <h1 className="text-2xl font-bold text-amber-700 mb-4">Conducteur introuvable</h1>
                    <p className="mb-3">
                        Le prompteur lit <code className="px-1 bg-slate-100 border border-slate-200 rounded">public{chemin}</code>,
                        une copie de <code className="px-1 bg-slate-100 border border-slate-200 rounded">docs/conferences/seminaire-CONDUCTEUR.md</code>.
                    </p>
                    <p className="mb-3 text-slate-500">Erreur : {erreur}</p>
                    <p>
                        La copie est faite automatiquement par <code className="px-1 bg-slate-100 border border-slate-200 rounded">npm run dev</code>.
                        Pour la refaire à la main : <code className="px-1 bg-slate-100 border border-slate-200 rounded">npm run conducteur</code>.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative w-full h-screen bg-white text-slate-900 flex flex-col font-sans">
            {/* ---------------------------------------------------- bandeau -- */}
            <header className="flex-shrink-0 border-b border-slate-200 bg-white/95 backdrop-blur px-5 py-3 flex items-center gap-5 flex-wrap">
                <div className="flex items-baseline gap-3 min-w-0">
                    <span className="text-3xl font-black text-sky-700 tabular-nums">
                        {num ?? '—'}
                    </span>
                    <span className="text-sm text-slate-400">/ {totalSlides}</span>
                    <span className="truncate text-lg font-semibold text-slate-900">
                        {sectionCourante?.titre || slideCourante?.title || ''}
                    </span>
                </div>

                <div className="ml-auto flex items-center gap-4 text-sm">
                    {/* chrono — le temps écoulé grisé à l'arrêt, pour qu'une
                        pause oubliée ne passe pas pour un chrono qui tourne */}
                    <div className="flex items-baseline gap-2">
                        <span className={`text-2xl font-bold tabular-nums ${enMarche ? 'text-slate-900' : 'text-slate-400'}`}>
                            {mmss(ecoule)}
                        </span>
                        {ecart != null && (
                            <span
                                className={`px-2 py-0.5 rounded text-xs font-bold tabular-nums ${ecart > 60 ? 'bg-amber-100 text-amber-800'
                                    : ecart < -60 ? 'bg-emerald-100 text-emerald-800'
                                        : 'bg-slate-100 text-slate-600'
                                    }`}
                                title="Écart à la fin prévue de cette slide — négatif, il reste du temps ; positif, tu débordes"
                            >
                                {ecart >= 0 ? '+' : '−'}{mmss(ecart)}
                            </span>
                        )}
                        {prevu != null && (
                            <span
                                className="text-xs text-slate-400"
                                title={prevuExact
                                    ? 'Heure de fin déclarée dans le conducteur'
                                    : "Répartie au prorata du texte entre les deux repères qui encadrent cette slide — le conducteur n'en donne pas pour celle-ci"}
                            >
                                fin {prevuExact ? '' : '~'}{mmss(prevu)}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={basculerChrono}
                            className={`px-3 h-8 rounded font-bold text-xs border transition-colors ${enMarche
                                ? 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                                : 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
                                }`}
                            title={enMarche ? 'Mettre le chrono en pause (p)' : 'Repartir (p)'}
                        >
                            {enMarche ? '❚❚ pause' : '▶ reprendre'}
                        </button>
                        <button
                            onClick={realigner}
                            disabled={debutPrevu == null}
                            className="px-2 h-8 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 text-xs text-slate-600 disabled:opacity-40 disabled:hover:bg-slate-100"
                            title={debutPrevu == null
                                ? "Aucun minutage connu pour cette slide"
                                : `Caler le chrono sur le début prévu de la slide ${num} — ${mmss(debutPrevu)} (r). Pour les répétitions : on reprend au milieu sans fausser l'écart.`}
                        >
                            ⇥ caler
                        </button>
                        <button
                            onClick={rembobiner}
                            className="px-2 h-8 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 text-xs text-slate-600"
                            title="Remettre le chrono à zéro et repartir"
                        >
                            ⟲
                        </button>
                    </div>

                    {/* relevé de répétition.
                        Le bouton PULSE tant qu'il enregistre : c'est la seule
                        fonction du prompteur qui, oubliée en marche ou oubliée
                        à l'arrêt, fait perdre une répétition entière. Le
                        compteur de slides à côté dit qu'il capte vraiment
                        quelque chose. */}
                    <div className="flex items-center gap-1">
                        <button
                            onClick={basculerEnregistrement}
                            className={`px-3 h-8 rounded font-bold text-xs border transition-colors ${enregistre
                                ? 'bg-rose-600 border-rose-600 text-white hover:bg-rose-700 animate-pulse'
                                : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                                }`}
                            title={enregistre
                                ? "Arrêter l'enregistrement des timings réels (e)"
                                : 'Enregistrer les timings réels de la répétition : chaque changement de slide est horodaté au chrono (e)'}
                        >
                            {enregistre ? '■ stop' : '● rec'}
                        </button>
                        <button
                            onClick={() => setReleveOuvert(true)}
                            disabled={marques.length === 0}
                            className="px-2 h-8 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 text-xs text-slate-600 disabled:opacity-40 disabled:hover:bg-slate-100 tabular-nums"
                            title={marques.length === 0
                                ? "Aucun relevé — lance d'abord un enregistrement"
                                : 'Ouvrir le relevé : durées réelles, écart au conducteur, débit mesuré'}
                        >
                            ⏱ {marques.filter((m) => m.num != null).length}
                        </button>
                    </div>

                    {/* taille */}
                    <div className="flex items-center gap-1">
                        <button onClick={() => setTaille((t) => Math.max(13, t - 1))} className="w-7 h-7 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-700">−</button>
                        <span className="w-9 text-center text-xs tabular-nums text-slate-500">{taille}px</span>
                        <button onClick={() => setTaille((t) => Math.min(48, t + 1))} className="w-7 h-7 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-700">+</button>
                    </div>

                    {/* ⚠️ PLUS DE BOUTON F1 (24/09). La voix IA et l'analyse
                        des questions sont jouées par la flèche suivante —
                        c'est le « → » ci-dessous qui dit, en couleur, ce que
                        fera le prochain appui. La touche F1 marche toujours,
                        en secours. */}

                    {/* navigation à distance.
                        « ⏭ » s'ARME sur les slides d'usine, comme F1 : sur une
                        slide ordinaire il fait la même chose que « → » et ne
                        mérite pas l'attention, sur une slide d'usine il est la
                        seule sortie rapide. Il reste cliquable partout — un
                        bouton qui se désactive à mi-parcours est un bouton sur
                        lequel on appuie deux fois en se demandant pourquoi. */}
                    <div className="flex items-center gap-1">
                        <button onClick={() => naviguer(-1)} className="px-3 h-8 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-700">←</button>
                        <button
                            onClick={() => naviguer(1, true)}
                            className={`px-3 h-8 rounded font-bold text-xs border transition-colors ${estUsine
                                ? 'bg-violet-500 border-violet-500 text-white hover:bg-violet-600'
                                : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200'
                                }`}
                            title={estUsine
                                ? 'Passer la manche : slide suivante sans dérouler la simulation (Maj+→)'
                                : 'Slide suivante sans laisser la simulation consommer la flèche (Maj+→)'}
                        >
                            ⏭ passer
                        </button>
                        {/* « → » ANNONCE CE QU'IL VA FAIRE. Sur une slide
                            dont l'action n'est pas encore jouée, le prochain
                            appui — ici, au clavier ou à la télécommande — la
                            joue au lieu de changer de slide : le bouton passe
                            en ambre (la voix) ou en violet (l'analyse). */}
                        <button
                            onClick={() => naviguer(1)}
                            className={`px-3 h-8 rounded text-white font-bold transition-colors ${actionSuivante === 'f1'
                                ? 'bg-amber-500 hover:bg-amber-600'
                                : actionSuivante === 'synthese'
                                    ? 'bg-violet-600 hover:bg-violet-700'
                                    : 'bg-sky-600 hover:bg-sky-700'
                                }`}
                            title={actionSuivante === 'f1'
                                ? 'Prochain appui : la voix IA. Le suivant changera de slide.'
                                : actionSuivante === 'synthese'
                                    ? "Prochain appui : lancer l'analyse des questions de la salle (20 à 60 s, rien ne bouge à l'écran). Le suivant changera de slide."
                                    : 'Slide suivante'}
                        >
                            {actionSuivante === 'f1' ? '→ voix' : actionSuivante === 'synthese' ? '→ analyse' : '→'}
                        </button>
                    </div>

                    <span
                        className={`flex items-center gap-2 text-xs font-semibold ${relie ? 'text-emerald-700' : 'text-amber-700'}`}
                        title={relie ? 'Synchronisé avec le diaporama' : "Aucun diaporama détecté sur cette origine"}
                    >
                        <span className={`w-2 h-2 rounded-full ${relie ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
                        {relie ? 'relié' : 'en attente'}
                    </span>
                    {nbDiaporamas > 1 && (
                        <span
                            className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-bold"
                            title="Plusieurs onglets de diaporama sont ouverts. Le prompteur ne pilote que celui qui est à l'écran : fermez les autres."
                        >
                            ⚠ {nbDiaporamas} diaporamas ouverts
                        </span>
                    )}

                    {/* Les questions de la salle : le compteur, le bouton de
                        synthèse et la liste. Posé ici, contre l'état de
                        connexion — ce sont les voyants qu'on balaie d'un œil
                        sans quitter le fil de sa phrase. Une conférence sans
                        `salle` déclarée n'en a pas. Voir CompteurSalle. */}
                    {salle && <CompteurSalle session={salle} />}

                    {/* Le QR, en grand, sur l'écran projeté. Reste allumé tant
                        qu'il est affiché : c'est le seul bouton du bandeau qui
                        change QUELQUE CHOSE À L'ÉCRAN de façon durable, il ne
                        doit pas pouvoir s'oublier. */}
                    {salle && (
                        <button
                            onClick={basculerQr}
                            className={`px-2 h-8 rounded border text-sm transition-colors ${qrOuvert
                                ? 'bg-cyan-500 border-cyan-500 text-white hover:bg-cyan-600 animate-pulse'
                                : 'border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            title={qrOuvert
                                ? "Retirer le QR de l'écran"
                                : "Afficher le QR des questions EN GRAND sur l'écran projeté, par-dessus la slide affichée — pour ceux qui arrivent en retard ou qui ont refermé la page"}
                        >
                            ⬛ QR
                        </button>
                    )}
                </div>
            </header>

            {/* avertissements de cohérence, uniquement s'il y en a */}
            {avertissements.length > 0 && (
                <div className="flex-shrink-0 bg-amber-50 border-b border-amber-300 px-5 py-2 text-xs text-amber-900">
                    ⚠️ Titres illisibles, ces sections sont perdues :{' '}
                    {avertissements.join(' · ')}
                </div>
            )}
            {couverture && (couverture.sansSection.length > 0 || couverture.horsDeck.length > 0) && (
                <div className="flex-shrink-0 bg-amber-50 border-b border-amber-300 px-5 py-2 text-xs text-amber-900">
                    {couverture.sansSection.length > 0 && (
                        <span className="mr-4">⚠️ Slides sans section : {couverture.sansSection.join(', ')}</span>
                    )}
                    {couverture.horsDeck.length > 0 && (
                        <span>⚠️ Sections visant une slide inexistante : {couverture.horsDeck.join(', ')}</span>
                    )}
                </div>
            )}

            {/* ------------------------------------------------------ corps -- */}
            {/* ⚠️ PAS DE REMBOURRAGE EN HAUT. Le titre de section collant se
                cale sous ce rembourrage, pas au bord de la zone : avec `py-4`,
                une bande de 16 px laissait passer, au-dessus du titre, la demi-
                ligne qui venait de défiler. Invisible tant que le défilement
                s'arrêtait toujours sur un titre ; flagrant depuis que le
                curseur le fait s'arrêter au milieu d'une section. */}
            <div
                ref={conteneurRef}
                onWheel={surDefilement}
                onTouchMove={surDefilement}
                className="flex-1 overflow-y-auto px-8 pb-4 scroll-smooth"
                style={{ fontSize: `${taille}px` }}
            >
                {markdown === null ? (
                    <p className="text-slate-400">Chargement du conducteur…</p>
                ) : (
                    <div className="max-w-6xl mx-auto pb-[60vh]">
                        {sections.map((s) => {
                            const cle = `${s.de}-${s.a}-${s.niveau}`;
                            const active = cle === cleCourante;
                            return (
                                <section
                                    key={cle}
                                    ref={(el) => { refsSections.current[cle] = el; }}
                                    className={`scroll-mt-4 transition-opacity duration-300 ${active ? 'opacity-100' : 'opacity-35 hover:opacity-75'
                                        }`}
                                >
                                    <h2
                                        className={`sticky top-0 z-10 -mx-8 px-8 py-[0.35em] mb-[0.35em] backdrop-blur text-[0.72em] font-bold uppercase tracking-wider ${active
                                            ? 'bg-sky-50 text-sky-800 border-y border-sky-300'
                                            : 'bg-white/95 text-slate-400 border-y border-transparent'
                                            }`}
                                    >
                                        <span className="tabular-nums">
                                            {s.de === s.a ? `Slide ${s.de}` : `Slides ${s.de} → ${s.a}`}
                                        </span>
                                        {s.titre ? <span className="ml-3 normal-case font-semibold">{s.titre}</span> : null}
                                    </h2>
                                    {/* Le texte est décalé à droite : la marge de gauche
                                        porte les repères de la slide, chacun en face
                                        de son paragraphe (voir MargeConducteur). */}
                                    <div className={PLACE_MARGE}>
                                        <RenduConducteur
                                            blocs={s.blocs}
                                            marge={s.marge}
                                            onF1={declencherF1}
                                            curseur={active ? curseur : null}
                                            refFilet={active ? filetRef : undefined}
                                            onChoisirBloc={active && chronoLance ? choisirBloc : undefined}
                                        />
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                )}
            </div>

            {releveOuvert && (
                <ReleveTimings
                    marques={marques}
                    minutage={minutage}
                    parSlide={parSlide}
                    onFermer={() => setReleveOuvert(false)}
                    onEffacer={effacerReleve}
                />
            )}

            {/* bouton de retour, seulement quand on s'est éloigné */}
            {!suivi && (
                <button
                    onClick={revenir}
                    className="absolute bottom-6 right-6 px-4 py-3 rounded-full bg-sky-600 text-white font-bold shadow-lg hover:bg-sky-700"
                >
                    ↓ revenir à la slide {num ?? ''}
                </button>
            )}
        </div>
    );
}
